import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from 'lexical';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect } from 'react';

import { INSERT_IMAGE_COMMAND } from '../../rich-lexical-editor/plugins/ImagePlugin';

const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

export default function ClipboardImagePlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');

  const processImageFile = useCallback(
    async (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          reject(
            new Error(
              t('error_reading_image_file') || 'Error reading image file'
            )
          );
        };
        reader.readAsDataURL(file);
      });
    },
    [t]
  );

  const handleClipboardPaste = useCallback(
    async (event: ClipboardEvent): Promise<boolean> => {
      const clipboardData = event.clipboardData;

      if (!clipboardData) {
        return false;
      }

      const { items } = clipboardData;
      // let hasImage = false;

      // Check if any clipboard items are images
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.kind === 'file' && SUPPORTED_IMAGE_TYPES.includes(item.type)) {
          // hasImage = true;
          const file = item.getAsFile();

          if (file) {
            try {
              // Process the image file and get data URL
              const imageDataUrl = await processImageFile(file);

              // Create a unique filename based on timestamp
              const timestamp = Date.now();
              // const extension = file.type.split('/')[1] || 'png';
              const altText = `Pasted image ${timestamp}`;

              // Insert the image using the existing image command
              editor.update(() => {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  src: imageDataUrl,
                  altText: altText,
                });
              });

              // Prevent default paste behavior since we handled the image
              event.preventDefault();
              return true;
            } catch (error) {
              return false;
            }
          }
        }
      }

      return false;
    },
    [editor, processImageFile]
  );

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // Check if there are any image items
        const hasImages = Array.from(clipboardData.items).some(
          (item) =>
            item.kind === 'file' && SUPPORTED_IMAGE_TYPES.includes(item.type)
        );

        if (hasImages) {
          // Handle async processing without blocking
          handleClipboardPaste(event);
          return true; // Indicate we're handling this event
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, handleClipboardPaste]);

  return null;
}
